import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Popover } from 'antd';
import { Api } from '../../../lib/api';
import { HardwareContext } from '../../../context/HardwareContext';

/**
 * Disconnection detection threshold.
 *
 * Physical devices send HTTP heartbeats to the server (POST /heartbeat),
 * which updates their `lastSeenDate` in the database. The frontend polls
 * GET /api/devices and compares `now - lastSeenDate`. If the device hasn't
 * sent a heartbeat within this threshold, it's considered disconnected.
 */
const DEVICE_DISCONNECTION_THRESHOLD_MS = 3 * 60 * 1000; // 3 minutes

class Device extends Component {
    static contextType = HardwareContext;

    static propTypes = {
        deviceId: PropTypes.number.isRequired
    };

    constructor() {
        super();

        this.state = {
            uptime: null,
            disconnected: false,
            lastSeenDate: null
        };
    }

    timeoutId = null;

    getDevice() {
        return this.context.devices.byId[this.props.deviceId];
    }

    getDeviceTypes() {
        return this.context.deviceTypes.byId;
    }

    componentDidMount() {
        const device = this.getDevice();
        this.setState({
            uptime: device?.uptime ?? null,
            lastSeenDate: device?.lastSeenDate ?? null
        });
        this.checkForDisconnection();
    }

    componentDidUpdate() {
        const device = this.getDevice();
        if (typeof device === 'undefined') {
            return;
        }

        // Check if uptime changed by comparing with state
        if (device.uptime !== this.state.uptime) {
            this.setState({ uptime: device.uptime });
        }

        // Check for disconnection only when lastSeenDate changes
        if (device.lastSeenDate !== this.state.lastSeenDate) {
            this.setState({ lastSeenDate: device.lastSeenDate });
            this.checkForDisconnection();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeoutId);
    }

    checkForDisconnection() {
        const device = this.getDevice();
        if (typeof device === 'undefined') {
            return;
        }

        let elapsedMs = new Date() - new Date(device.lastSeenDate);

        if (elapsedMs > DEVICE_DISCONNECTION_THRESHOLD_MS) {
            clearTimeout(this.timeoutId);
            if (!this.state.disconnected) {
                this.setState({ disconnected: true });
            }
        } else {
            // Reset timeout
            clearTimeout(this.timeoutId);

            // Display as disconnected once the threshold has been exceeded
            this.timeoutId = setTimeout(() => {
                this.setState({ disconnected: true });
            }, DEVICE_DISCONNECTION_THRESHOLD_MS - elapsedMs);

            if (this.state.disconnected) {
                this.setState({ disconnected: false });
            }
        }
    }

    onDragStart = (e, deviceId, deviceType) => {
        if (!(deviceType === 'USB Test and Measurement Device (IVI)')) {
            e.dataTransfer.setData('text/plain', deviceId);
        }
    };

    formattedUptime() {
        let uptime = this.state.uptime;

        // less than 1 day
        if (uptime < 86400) {
            let hours = ('0' + Math.floor(uptime / 3600)).slice(-2);
            let minutes = ('0' + (Math.floor(uptime / 60) % 60)).slice(-2);
            return hours + ':' + minutes;
        }

        // Less than 1 week
        if (uptime < 604800) {
            let days = Math.floor(uptime / 86400) + 'd';
            let hours = (Math.floor(uptime / 3600) % 24) + 'h';
            return days + ' ' + hours;
        }

        // Less than 1 year (i.e. < 365 days)
        if (uptime < 31536000) {
            let weeks = Math.floor(uptime / 604800) + 'w';
            let days = (Math.floor(uptime / 86400) % 7) + 'd';
            return weeks + ' ' + days;
        }

        // 1 year or more (i.e. >= 365 days)
        let years = Math.floor(uptime / 31536000) + 'y';
        let weeks = Math.floor((uptime % 31536000) / 604800) + 'w';
        return years + ' ' + weeks;
    }

    render() {
        const device = this.getDevice();
        const deviceTypes = this.getDeviceTypes();
        const { fetchDevices, fetchStations } = this.context;

        if (typeof device === 'undefined') {
            return <React.Fragment></React.Fragment>;
        }

        let deviceTypeId = device && device.deviceTypeId ? device.deviceTypeId : null;
        let deviceType = deviceTypeId in deviceTypes ? deviceTypes[deviceTypeId].name : '';

        let ipAddress = '';
        let ipAddressUrl = '';
        let switch_1 = null;
        let numDet = null;
        let switch_2 = null;
        let wl = null;

        if (device !== undefined) {
            // Prepare IP address
            if (device.ipAddress !== null) {
                // Assume it's an IPv6 address if it contains a colon
                let ipv6 = device.ipAddress && device.ipAddress.indexOf(':') === -1 ? false : true;
                ipAddress = ipv6 ? '[' + device.ipAddress + ']' : device.ipAddress;

                // Add port if it's needed
                if (device.port && device.port !== 80) {
                    ipAddress += ':' + device.port;
                }

                ipAddressUrl = 'http://' + ipAddress;
            }

            if (device.wl !== null) {
                wl = device.wl;
            }

            if (device.numDet !== null) {
                numDet = device.numDet;
            }

            if (device.SW1 !== null) {
                switch_1 = device.SW1.split(',');
            }
            if (device.SW2 !== null) {
                switch_2 = device.SW2.split(',');
            }
        }

        return (
            <div className="device" draggable="true" onDragStart={(e) => this.onDragStart(e, this.props.deviceId, deviceType)}>
                <div className="container grabbable">
                    <div className=" title ">
                        {deviceType === 'USB Test and Measurement Device (IVI)' ? (
                            <h4>RL1</h4>
                        ) : (
                            <h4>
                                {device.partnum ? device.partnum : device.model}
                                &nbsp;
                                <Popover
                                    content={
                                        device.model === 'RL1' ? (
                                            <ul>
                                                <li>
                                                    <div style={{ display: 'flex' }}>
                                                        Detectors :&nbsp;
                                                        <div style={{ fontStyle: 'italic', color: 'blue' }}>{numDet}</div>
                                                    </div>
                                                </li>
                                                <li>
                                                    <div style={{ display: 'flex' }}>
                                                        Wavelengths:&nbsp;
                                                        <div style={{ fontStyle: 'italic', color: 'blue' }}> {wl}</div>
                                                    </div>
                                                </li>
                                                {switch_1 && switch_1[switch_1.length - 1] !== '0' && (
                                                    <li>
                                                        <div style={{ display: 'flex' }}>
                                                            Switch #1:&nbsp;
                                                            <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                                {switch_1[switch_1.length - 1]}
                                                                &nbsp; channels
                                                            </div>
                                                        </div>
                                                    </li>
                                                )}
                                                {switch_2 && switch_2[switch_2.length - 1] !== '0' && (
                                                    <li>
                                                        <div style={{ display: 'flex' }}>
                                                            Switch #2: &nbsp;
                                                            <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                                {switch_2[switch_2.length - 1]} &nbsp; channels
                                                            </div>
                                                        </div>
                                                    </li>
                                                )}
                                            </ul>
                                        ) : (
                                            <ul>
                                                <li>
                                                    <div style={{ display: 'flex' }}>
                                                        Channels:&nbsp;
                                                        <div style={{ fontStyle: 'italic', color: 'blue' }}>
                                                            {device.numChannels}
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        )
                                    }
                                    title={
                                        device.model === 'PT1'
                                            ? 'PT1'
                                            : device.mode === 'MM'
                                            ? 'Multi-Mode'
                                            : 'Single-Mode'
                                    }
                                >
                                    <InfoCircleOutlined style={{ cursor: 'pointer' }} />
                                </Popover>
                            </h4>
                        )}
                    </div>
                    <div
                        className=" icon"
                        onClick={() => {
                            Api.deleteDevice(device.id).then(() => {
                                fetchDevices();
                                fetchStations();
                            });
                        }}
                    >
                        <CloseCircleOutlined />
                    </div>

                    <div className="image-box">
                        {(deviceType === 'RL1' ||
                            deviceType === 'RLM' ||
                            deviceType === 'USB Test and Measurement Device (IVI)') && (
                            <a href={ipAddressUrl === '' ? null : ipAddressUrl} target="_blank" rel="noopener noreferrer">
                                <img src={'/RLM.png'} alt="Logo" />
                            </a>
                        )}
                        {(deviceType === 'PT1' || deviceType === 'PTM') && (
                            <a href={ipAddressUrl === '' ? null : ipAddressUrl} target="_blank" rel="noopener noreferrer">
                                <img src={'/PTM.png'} alt="Logo" />
                            </a>
                        )}
                        {deviceType === 'SX1' && (
                            <a href={ipAddressUrl === '' ? null : ipAddressUrl} target="_blank" rel="noopener noreferrer">
                                <img src={'/OSX.png'} alt="Logo" />
                            </a>
                        )}
                        {deviceType === 'BR1' && (
                            <a href={ipAddressUrl === '' ? null : ipAddressUrl} target="_blank" rel="noopener noreferrer">
                                <img src={'/BRM.png'} alt="Logo" />
                            </a>
                        )}

                        <a href={ipAddressUrl === '' ? null : ipAddressUrl} target="_blank" rel="noopener noreferrer">
                            {ipAddress}
                        </a>
                    </div>
                    <div className="info-box">
                        {' '}
                        <p>
                            {' '}
                            Firmware: <span>{device.firmwareVersion}</span>{' '}
                        </p>
                        <p>Serial Number: {device === undefined ? '' : <span> {device.uid}</span>}</p>
                        <p>
                            Status:{' '}
                            {device === undefined ? (
                                ''
                            ) : this.state.disconnected ? (
                                <Tag color="red">Disconnected</Tag>
                            ) : (
                                <Tag color="green">
                                    {device.status === 'Manual Visual Fault Locator' ? (
                                        <>VFL Mode</>
                                    ) : (
                                        <>{device.status} </>
                                    )}
                                </Tag>
                            )}
                        </p>
                    </div>
                </div>
            </div>
        );
    }
}

export default Device;
